import React, { useState } from 'react';
import { X, Mail, Loader2 } from 'lucide-react';

interface ConfirmResendModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    user: {
        firstName: string;
        lastName: string;
        email: string;
    } | null;
}

const ConfirmResendModal: React.FC<ConfirmResendModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    user
}) => {
    const [isSending, setIsSending] = useState(false);

    const handleConfirm = async () => {
        setIsSending(true);
        try {
            await onConfirm();
        } finally {
            setIsSending(false);
        }
    };

    if (!isOpen || !user) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(217,119,6,0.1)' }}>
                            <Mail className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Resend Invitation</h3>
                            <p className="text-sm text-gray-500">Send a new invitation email</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isSending}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-sm text-gray-700 mb-4">
                        Are you sure you want to resend the invitation to <strong>{user.firstName} {user.lastName}</strong>?
                    </p>

                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                            <li>A new invitation email will be sent to <span className="font-mono">{user.email}</span></li>
                            <li>The previous invite link will be invalidated</li>
                            <li>The new link expires in 48 hours</li>
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 px-6 pb-6">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSending}
                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isSending}
                        className="px-4 py-2 text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors disabled:opacity-50 flex items-center"
                    >
                        {isSending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {isSending ? 'Sending...' : 'Resend Invitation'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmResendModal;
